import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { devLog } from '@/utils/logger';

/**
 * NotFound Page
 *
 * 404 error page with Agile Assets brand personality
 */

export const NotFound = () => {
  const { t } = useTranslation('common');

  return (
    <div className="min-h-screen flex items-center justify-center p-4 auth-bg">
      {/* 404 Card */}
      <div className="relative bg-white rounded-lg shadow-2xl p-8 w-full max-w-lg">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src="/images/logo.png"
            alt="Ägile Ässets"
            className="w-full max-w-[45%] h-auto"
            onError={(e) => {
              devLog.error('Logo failed to load from:', e.currentTarget.src);
              e.currentTarget.onerror = null;
              // Fallback to text logo
              e.currentTarget.style.display = 'none';
              const parent = e.currentTarget.parentElement;
              if (parent && !parent.querySelector('.text-logo')) {
                const textLogo = document.createElement('div');
                textLogo.className = 'text-logo text-4xl font-bold text-primary-500';

                const primaryText = document.createElement('span');
                primaryText.className = 'text-primary-500';
                primaryText.textContent = 'Ä';

                const dotsText = document.createElement('span');
                dotsText.className = 'text-xs align-top';
                dotsText.textContent = '••';

                textLogo.appendChild(primaryText);
                textLogo.appendChild(dotsText);
                parent.appendChild(textLogo);
              }
            }}
          />
        </div>

        {/* Animated 404 Display */}
        <div className="text-center mb-6">
          <div className="relative inline-block">
            <h1
              className="text-[120px] font-black leading-none font-wagner select-none bg-gradient-mint text-transparent bg-clip-text"
              style={{
                animation: 'float 3s ease-in-out infinite',
              }}
            >
              {t('notfound_error_code')}
            </h1>
            {/* Subtle glow effect */}
            <div
              className="absolute inset-0 blur-xl opacity-30 -z-10 bg-gradient-mint"
              style={{
                animation: 'pulse 2s ease-in-out infinite',
              }}
            />
          </div>
        </div>

        {/* Playful Tagline */}
        <div className="text-center mb-4">
          <p className="text-[11px] uppercase tracking-wider text-gray-900 leading-relaxed font-medium">
            {t('notfound_tagline_1')}
            <br />
            {t('notfound_tagline_2')}{' '}
            <span className="text-primary-500 font-bold">{t('notfound_tagline_highlight')}</span>.
          </p>
        </div>

        {/* Main Heading */}
        <h2 className="text-[24px] font-bold text-gray-900 text-center mb-3 font-wagner">
          {t('notfound_title')}
        </h2>

        {/* Subtext */}
        <p className="text-sm text-gray-600 text-center mb-8">
          {t('notfound_message')}
        </p>

        {/* CTA Button */}
        <div className="text-center mb-6">
          <Link
            to="/"
            className="inline-block bg-bs-secondary hover:bg-secondary-600 text-secondary-contrast font-semibold px-8 py-3 rounded-lg uppercase tracking-wide text-sm transition-all duration-200 shadow-md hover:shadow-lg"
          >
            {t('notfound_go_home')}
          </Link>
        </div>

        {/* Footer */}
        <div className="pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            {t('notfound_footer')}{' '}
            <span className="text-secondary-500 font-medium">{t('notfound_footer_highlight')}</span>{' '}
            {t('notfound_footer_end')}
          </p>
        </div>

        {/* CSS Animations */}
        <style>{`
          @keyframes float {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-10px);
            }
          }

          @keyframes pulse {
            0%, 100% {
              opacity: 0.2;
            }
            50% {
              opacity: 0.4;
            }
          }
        `}</style>
      </div>
    </div>
  );
};
